
import { GoogleGenAI, Type } from "@google/genai";
import { StudentProfile, ProfessionalProfile, ChatRequest } from "../types.ts";

const parseJson = (text: string | undefined) => {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (e) {
    return null;
  }
};

const shouldFallback = (error: any, context: string) => {
  console.warn(`[Sage AI] Fallback triggered for ${context}. Providing simulated high-fidelity data. Error: ${error.message}`);
  return true;
};

// --- MOCK DATA GENERATORS ---

const getMockCareerStrategy = (aspirations: string) => ({
  industryBenchmarks: ["Senior Systems Architect (HFT)", "Quantitative Developer", "Low-Latency Engineer"],
  roadmap: {
    phase1ShortTerm: {
      title: "Foundation & Core Competency",
      timeline: "Months 1-6",
      searchKeywords: ["Junior C++ Developer", "Systems Intern", "FPGA Engineer"],
      tasks: [
        { description: "Master Modern C++ (C++17/20) and STL containers.", recruitingTimeline: "Continuous" },
        { description: "Build a market data feed handler using UDP multicast.", recruitingTimeline: "Project Portfolio" },
        { description: "Secure internship at a prop trading shop or fintech.", recruitingTimeline: "Aug-Nov Recruiting Cycle" }
      ],
      certifications: ["C++ Certified Professional Programmer (CPP)"],
      recommendedTraining: ["Coursera: Accelerated CS Fundamentals", "System Design for HFT"]
    },
    phase2MidTerm: {
      title: "Specialization & Production Hardening",
      timeline: "Years 1-2",
      searchKeywords: ["Quant Dev", "HFT Systems Engineer"],
      tasks: [
        { description: "Optimize kernel bypass networking (Solarflare/OpenOnload).", recruitingTimeline: "On-the-job" },
        { description: "Contribute to open-source low-latency libraries.", recruitingTimeline: "Continuous" },
        { description: "Transition to a desk-aligned engineering role.", recruitingTimeline: "Internal Mobility" }
      ],
      certifications: ["Linux Kernel Internals"],
      recommendedTraining: ["Advanced Computer Architecture"]
    },
    phase3LongTerm: {
      title: "Architectural Leadership",
      timeline: "Years 3-5+",
      searchKeywords: ["Lead Architect", "Head of Trading Technology"],
      tasks: [
        { description: "Design next-gen execution strategies on FPGA.", recruitingTimeline: "Strategic" },
        { description: "Lead a team of quant developers.", recruitingTimeline: "Management Track" }
      ],
      certifications: ["CFA Level 1 (Optional for context)"],
      recommendedTraining: ["Leadership for Engineers"]
    }
  }
});

const getMockMeetingPrep = (profName: string) => ({
  deepAnchors: [
    { title: "Shared Interest", insight: "You both have a background in scalable distributed systems.", icon: "fa-server" },
    { title: "Career Transition", insight: `${profName} moved from Big Tech to Finance, similar to your goal.`, icon: "fa-exchange-alt" }
  ],
  reverseMentorship: {
    professionalBlindSpot: "Modern LLM-driven coding workflows.",
    studentValueHook: "Share how you use Copilot/Cursor for rapid prototyping.",
    howToPitch: "Ask if their compliance team allows AI tools, then share your workflow."
  },
  agenda: [
    { time: "0-5 min", topic: "Rapport Building", details: "Connect on the 'Shared Interest' anchor.", relationalGoal: "Warmth" },
    { time: "5-15 min", topic: "The Pivot", details: "Ask specifically about the transition challenges.", relationalGoal: "Competence" },
    { time: "15-25 min", topic: "Technical Deep Dive", details: "Discuss specific project challenges.", relationalGoal: "Depth" },
    { time: "25-30 min", topic: "Next Steps", details: "Ask for one specific actionable advice.", relationalGoal: "Continuity" }
  ],
  personalizedImpactQuestions: [
    { question: "What was the hardest technical habit to unlearn when you switched industries?", psychology: "Vulnerability & Growth Mindset", hookDetail: "Transition Context" },
    { question: "How do you see the role of FPGA evolving with improved CPU caches?", psychology: "Technical Insight", hookDetail: "Industry Trend" },
    { question: "What is one non-technical skill that saved you in a crisis?", psychology: "War Stories", hookDetail: "Resilience" },
    { question: "Who do you read to stay ahead of the market microstructure changes?", psychology: "Curiosity", hookDetail: "Learning Source" }
  ]
});

export const aiService = {
  async coachDraftMessage(student: StudentProfile | undefined, professional: ProfessionalProfile | undefined, goal: string) {
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `AI Mentorship Coach. Help this student reach out to a professional. 
        Student: ${JSON.stringify(student || {})}
        Professional: ${JSON.stringify(professional || {})}
        Goal: ${goal}
        Context: The student is in a chat interface. Generate a specific message draft they can send immediately.`,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              messageDraft: { type: Type.STRING },
              talkingPoints: { type: Type.ARRAY, items: { type: Type.STRING } },
              suggestedQuestions: { type: Type.ARRAY, items: { type: Type.STRING } }
            }
          }
        }
      });
      return parseJson(response.text) || { messageDraft: goal, talkingPoints: [], suggestedQuestions: [] };
    } catch (error) {
      if (shouldFallback(error, 'Coach Draft')) {
        return { 
          messageDraft: `Hi ${professional?.fullName?.split(' ')[0] || 'there'}, I'm ${student?.userId ? 'a student' : 'Alex'} at ${student?.school || 'university'}. I've been following your work at ${professional?.company} and your journey in ${professional?.industry}. ${goal}.`,
          talkingPoints: ["Role transition", "Industry skills", "Company culture"],
          suggestedQuestions: ["What's your typical day like?", "Advice for graduates?", "How to stand out?"]
        };
      }
      return null;
    }
  },

  async getRecommendations(student: StudentProfile | undefined, professionals: ProfessionalProfile[], prioritizeAlumni: boolean = false) {
    try {
      if (!student) return [];
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      
      const minProfs = professionals.map(p => ({
        id: p.userId,
        name: p.fullName,
        currentTitle: p.title,
        currentCompany: p.company,
        industry: p.industry,
        gradUniversity: p.gradUniversity,
        workHistory: p.workHistory?.map(w => `${w.role} at ${w.company}`),
        topics: p.topics,
        city: p.city,
        targetGPA: p.targetStudentGPA,
        targetIndustry: p.targetStudentIndustry,
        targetDegree: p.targetStudentDegree
      }));

      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `SYSTEM: You are Sage, an Executive Career Architect and Matchmaker.
        
        GOAL: Identify high-impact mentorship connections by deeply analyzing the *trajectory* of the professional against the *aspirations* of the student.
        
        STUDENT PROFILE: 
        - Goal: "${student.goals}"
        - Aspiring Role: "${student.aspiringRole}"
        - Major: "${student.major}"
        - Interests: ${JSON.stringify(student.interests)}
        - Mentorship Topics Wanted: ${JSON.stringify(student.mentorshipTopics)}
        - Target Networking City: "${student.networkingCity || 'Any'}"
        - School: "${student.school}"

        MENTOR POOL: ${JSON.stringify(minProfs)}
        
        USER SETTINGS:
        - Prioritize Alumni: ${prioritizeAlumni}

        ANALYSIS PROTOCOL:
        1. **Trajectory Mirroring (CRITICAL)**: Does the professional hold the role the student wants, or have they held it in the past? (e.g. Student wants "HFT Architect", Pro was "C++ dev at Citadel"). This is the #1 factor.
        2. **Current Seat Advantage**: Analyze exactly what their *current* specific job title allows them to do for the student.
        3. **Skill Gap Bridge**: Do they teach topics (in 'topics' array) that fill the student's specific gaps?
        4. **Institutional Pedigree**: 
           - IF 'Prioritize Alumni' is TRUE: heavily weight shared 'gradUniversity' or 'school'.
           - IF 'Prioritize Alumni' is FALSE: Treat shared university as a minor nice-to-have, NOT a driver. Focus on goals and industry alignment.
        5. **Geographic Strategy**: If the student lists a 'Target Networking City', prioritize professionals located there.

        OUTPUT REQUIREMENTS:
        - **Score (0-100)**: Be critical.
        - **Rationale**: Specific hook based on trajectory/goals.
        - **Current Role Value**: How their CURRENT JOB helps.
        - **Synergy Thesis**: Technical explanation of the fit.

        Return JSON with matches sorted by score.`,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              matches: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    professionalId: { type: Type.STRING },
                    score: { type: Type.NUMBER },
                    rationale: { type: Type.STRING },
                    currentRoleValue: { type: Type.STRING },
                    synergyThesis: { type: Type.STRING },
                    matchType: { type: Type.STRING, enum: ["TRAJECTORY_MIRROR", "SKILL_BRIDGE", "ALUMNI_LINK", "INDUSTRY_VETERAN"] }
                  }
                }
              }
            }
          }
        }
      });
      const data = parseJson(response.text);
      return data?.matches || [];
    } catch (error) {
      if (shouldFallback(error, 'Recommendations')) {
         return professionals.map((p) => {
          let score = 65;
          let rationale = "Industry overlap detected.";
          let thesis = "This student aligns with your general mentorship profile.";
          let roleValue = `As a ${p.title}, they can provide industry-standard guidance.`;

          const sGPA = parseFloat(student?.gpa || "0");
          const pGPATarget = p.targetStudentGPA === 'any' ? 0 : parseFloat(p.targetStudentGPA || "0");
          
          if (pGPATarget > 0 && sGPA >= pGPATarget) { score += 10; }
          
          if (student?.networkingCity && p.city.toLowerCase().includes(student.networkingCity.toLowerCase())) {
             score += 15;
             rationale = `Matches your target networking city: ${p.city}.`;
          }
          if (student?.aspiringRole && (p.title.toLowerCase().includes(student.aspiringRole.toLowerCase()) || p.industry.toLowerCase().includes(student.aspiringRole.toLowerCase()))) {
             score += 20;
             rationale += ` Strong Role alignment with ${p.title}.`;
             roleValue = `Since they are currently a ${p.title}, they can offer specific roadmap advice for your goal of becoming one.`;
          }
          // Only boost score for university if explicitly requested or if it's a tie-breaker
          if (prioritizeAlumni && student?.school && p.gradUniversity?.includes(student.school)) {
             score += 15;
             rationale += ` Alumni of ${student.school}.`;
          }

          return { professionalId: p.userId, score: Math.min(score, 99), rationale, currentRoleValue: roleValue, synergyThesis: thesis, matchType: "INDUSTRY_VETERAN" };
        }).sort((a, b) => b.score - a.score);
      }
      return [];
    }
  },

  async getMenteeRecommendations(professional: ProfessionalProfile, students: StudentProfile[]) {
    try {
      // Basic filtering still applies
      const filtered = students.filter(s => {
        if (professional.targetStudentGPA && professional.targetStudentGPA !== 'any') {
           const target = parseFloat(professional.targetStudentGPA);
           const studentGPA = parseFloat(s.gpa || "0");
           if (studentGPA < target) return false;
        }
        return true;
      });

      if (filtered.length === 0) return [];

      const minStudents = filtered.map(s => ({
        id: s.userId,
        name: s.fullName,
        major: s.major,
        interests: s.interests,
        goals: s.goals
      }));

      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `SYSTEM: Mentorship Matchmaker.
        GOAL: Find mentees for this professional.
        PRO: ${JSON.stringify(professional)}
        STUDENTS: ${JSON.stringify(minStudents)}
        
        OUTPUT: JSON array of matches with score, rationale, talkingPoint.`,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              matches: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    studentId: { type: Type.STRING },
                    score: { type: Type.NUMBER },
                    rationale: { type: Type.STRING },
                    talkingPoint: { type: Type.STRING }
                  }
                }
              }
            }
          }
        }
      });
      return parseJson(response.text)?.matches || [];
    } catch (error) { return []; }
  },

  async getReverseMentorshipRecommendations(professional: ProfessionalProfile, students: StudentProfile[]) {
    try {
      const minStudents = students.map(s => ({
        id: s.userId,
        name: s.fullName,
        major: s.major,
        school: s.school,
        interests: s.interests,
        bio: s.bio
      }));

      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `SYSTEM: You are an "Innovation Scout" for an executive professional.
        
        GOAL: Identify specific "Blind Spots" in the professional's profile (Legacy skills, Traditional Industries) and match them with students who have "Emerging Skills" (e.g., GenAI, TikTok Trends, DeFi, Rust, No-Code) to create a high-value "Knowledge Exchange".
        
        IGNORE GPA. IGNORE DEGREE. Focus purely on **Complementary Skills**.

        PROFESSIONAL PROFILE:
        - Title: ${professional.title}
        - Industry: ${professional.industry}
        - Expertise: ${JSON.stringify(professional.topics)}
        - Years Experience: ${professional.yearsExperience}

        STUDENT POOL: ${JSON.stringify(minStudents)}

        ANALYSIS PROTOCOL:
        1. **Detect the Gap**: Where is the professional potentially outdated? (e.g. Traditional Marketing -> Gap: TikTok Algo).
        2. **Find the Expert**: Which student is a "native" in that gap area?
        3. **Formulate the Exchange**: "You teach them [Professional's Strength], They teach you [Student's Unique Skill]."

        OUTPUT REQUIREMENTS:
        - **Score**: 0-100 match strength based on the *value of the exchange*.
        - **Rationale**: Explain the exchange. e.g., "While you are an expert in Traditional Finance, this student is building DAOs. Perfect for understanding the DeFi threat landscape."
        - **ProfessionalGap**: 2-3 words describing the pro's blind spot (e.g. "Web3 Literacy").
        - **StudentEdge**: 2-3 words describing the student's unique value (e.g. "Smart Contracts").
        - **TalkingPoint**: A question to initiate the swap.

        Return JSON with matches.`,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              matches: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    studentId: { type: Type.STRING },
                    score: { type: Type.NUMBER },
                    rationale: { type: Type.STRING },
                    professionalGap: { type: Type.STRING },
                    studentEdge: { type: Type.STRING },
                    talkingPoint: { type: Type.STRING }
                  }
                }
              }
            }
          }
        }
      });
      
      const data = parseJson(response.text);
      return data?.matches || [];

    } catch (error) {
      if (shouldFallback(error, 'Reverse Mentorship')) {
         return students.map(s => ({
            studentId: s.userId,
            score: 80,
            rationale: `As a digital native in ${s.major}, ${s.fullName} offers a fresh perspective on ${s.interests[0] || 'modern tech'} that complements your executive experience.`,
            professionalGap: "Emerging Tech",
            studentEdge: "Digital Native",
            talkingPoint: `Ask them how their generation perceives ${professional.industry} trends.`
         }));
      }
      return [];
    }
  },

  async generateCareerStrategy(profile: any, aspirations: string) {
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `You are a Career Benchmarking AI. Based on the aspiration: "${aspirations}" and profile: ${JSON.stringify(profile)}, generate a 3-phase roadmap. 
        
        CRITICAL:
        1. BASE STEPS ON REAL-LIFE TRAJECTORIES.
        2. PROVIDE SEARCH KEYWORDS.
        3. CREDENTIALS & RECRUITING TIMELINES.`,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              industryBenchmarks: { type: Type.ARRAY, items: { type: Type.STRING } },
              roadmap: {
                type: Type.OBJECT,
                properties: {
                  phase1ShortTerm: { 
                    type: Type.OBJECT, 
                    properties: { 
                      title: { type: Type.STRING }, 
                      timeline: { type: Type.STRING },
                      searchKeywords: { type: Type.ARRAY, items: { type: Type.STRING } },
                      tasks: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { description: { type: Type.STRING }, recruitingTimeline: { type: Type.STRING } } } },
                      certifications: { type: Type.ARRAY, items: { type: Type.STRING } },
                      recommendedTraining: { type: Type.ARRAY, items: { type: Type.STRING } }
                    } 
                  },
                  phase2MidTerm: { 
                    type: Type.OBJECT, 
                    properties: { 
                      title: { type: Type.STRING }, 
                      timeline: { type: Type.STRING },
                      searchKeywords: { type: Type.ARRAY, items: { type: Type.STRING } },
                      tasks: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { description: { type: Type.STRING }, recruitingTimeline: { type: Type.STRING } } } },
                      certifications: { type: Type.ARRAY, items: { type: Type.STRING } },
                      recommendedTraining: { type: Type.ARRAY, items: { type: Type.STRING } }
                    } 
                  },
                  phase3LongTerm: { 
                    type: Type.OBJECT, 
                    properties: { 
                      title: { type: Type.STRING }, 
                      timeline: { type: Type.STRING },
                      searchKeywords: { type: Type.ARRAY, items: { type: Type.STRING } },
                      tasks: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { description: { type: Type.STRING }, recruitingTimeline: { type: Type.STRING } } } },
                      certifications: { type: Type.ARRAY, items: { type: Type.STRING } },
                      recommendedTraining: { type: Type.ARRAY, items: { type: Type.STRING } }
                    } 
                  }
                }
              }
            }
          }
        }
      });
      return parseJson(response.text);
    } catch (error) { 
      if (shouldFallback(error, 'Career Strategy')) {
        return getMockCareerStrategy(aspirations);
      }
      return null; 
    }
  },

  async generateMeetingPrep(student: StudentProfile | undefined, professional: ProfessionalProfile | undefined, requestDetails: ChatRequest) {
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `Relationship-First Career Architect. 
        STUDENT: ${JSON.stringify(student || {})}
        PROFESSIONAL: ${JSON.stringify(professional || {})}
        
        Generate a meeting preparation guide with a focus on high-impact questions.`,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              deepAnchors: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { title: { type: Type.STRING }, insight: { type: Type.STRING }, icon: { type: Type.STRING } } } },
              reverseMentorship: { type: Type.OBJECT, properties: { professionalBlindSpot: { type: Type.STRING }, studentValueHook: { type: Type.STRING }, howToPitch: { type: Type.STRING } } },
              agenda: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { time: { type: Type.STRING }, topic: { type: Type.STRING }, details: { type: Type.STRING }, relationalGoal: { type: Type.STRING } } } },
              personalizedImpactQuestions: { 
                  type: Type.ARRAY, 
                  items: { 
                      type: Type.OBJECT, 
                      properties: { 
                          question: { type: Type.STRING }, 
                          psychology: { type: Type.STRING }, 
                          hookDetail: { type: Type.STRING } 
                      } 
                  } 
              }
            }
          }
        }
      });
      return parseJson(response.text);
    } catch (error) { 
      if (shouldFallback(error, 'Meeting Prep')) {
        return getMockMeetingPrep(professional?.fullName || "Professional");
      }
      return null; 
    }
  },

  async getJobsAndCoops(student: StudentProfile | undefined) {
    try {
      if (!student) return [];
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `AI Jobs for: ${JSON.stringify(student)}`,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: { jobs: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { id: { type: Type.STRING }, title: { type: Type.STRING }, company: { type: Type.STRING }, location: { type: Type.STRING }, type: { type: Type.STRING }, industry: { type: Type.STRING }, description: { type: Type.STRING }, matchScore: { type: Type.NUMBER }, sourceUrl: { type: Type.STRING } } } } }
          }
        }
      });
      return parseJson(response.text)?.jobs || [];
    } catch (error) { return []; }
  },

  async getEvents(student: StudentProfile | undefined) {
    try {
      if (!student) return [];
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `AI Events for: ${JSON.stringify(student)}`,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: { events: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { id: { type: Type.STRING }, title: { type: Type.STRING }, organizer: { type: Type.STRING }, date: { type: Type.STRING }, location: { type: Type.STRING }, type: { type: Type.STRING }, sourceUrl: { type: Type.STRING } } } } }
          }
        }
      });
      return parseJson(response.text)?.events || [];
    } catch (error) { 
      if (shouldFallback(error, 'Events')) {
        return [
          {
            id: 'ev1',
            title: 'FinTech Futures Summit',
            organizer: 'Rotman Commerce',
            date: 'Oct 12 • 2:00 PM',
            location: 'University of Toronto, St. George',
            type: 'Campus Mixer',
            sourceUrl: '#'
          },
          {
            id: 'ev2',
            title: 'Deep Learning & Coffee',
            organizer: 'Vector Institute',
            date: 'Oct 14 • 10:00 AM',
            location: 'MaRS Discovery District, Toronto',
            type: 'Industry Workshop',
            sourceUrl: '#'
          },
          {
            id: 'ev3',
            title: 'Global Markets Virtual Sync',
            organizer: 'Goldman Sachs Campus Recruiting',
            date: 'Oct 15 • 5:00 PM',
            location: 'Virtual / Zoom',
            type: 'Recruiting Webinar',
            sourceUrl: '#'
          },
          {
            id: 'ev4',
            title: 'StartUp Grind Toronto',
            organizer: 'Google for Startups',
            date: 'Oct 20 • 6:00 PM',
            location: 'OneEleven, Toronto',
            type: 'Networking Mixer',
            sourceUrl: '#'
          },
          {
            id: 'ev5',
            title: 'Engineering Science Career Fair',
            organizer: 'UofT Engineering',
            date: 'Oct 22 • 11:00 AM',
            location: 'University of Toronto, Exam Centre',
            type: 'Campus Career Fair',
            sourceUrl: '#'
          }
        ];
      }
      return []; 
    }
  },

  async generatePostCallFollowUp(student: StudentProfile | undefined, professional: ProfessionalProfile | undefined, summary: string) {
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `AI Follow-up for: ${summary}`,
        config: {
          responseMimeType: "application/json",
          responseSchema: { type: Type.OBJECT, properties: { followUpDraft: { type: Type.STRING } } }
        }
      });
      return parseJson(response.text) || { followUpDraft: "Thanks for the chat!" };
    } catch (error) { return { followUpDraft: "Thank you for the chat!" }; }
  }
};
